import { useEffect, useMemo, useState } from 'react';
import Dotdotdot from 'react-dotdotdot';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';
import { PillsList } from '@hivebrite/design-system/PillsList';
import type { Theme } from '@hivebrite/design-system/Theme';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import type { PublicationLayoutsProps } from 'hb-builder/components/BuilderComponents/dynamic/Publication/PublicationLayouts';
import { JoinGroupButton } from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/TopicsWrapper/JoinGroupButton';
import RequestGroupModal from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/TopicsWrapper/RequestGroupModal';
import PublicationCommonLoader from 'hb-builder/components/common/LoadingComponents/PublicationCommonLoader';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { GoToGroupButtonWithoutContext } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicActions/GoToGroupButton';
import { TopicDetailsModalWithoutContext } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicDetailsModal';
import { getTopicImmersiveMenuHref } from 'hb-react/shared/apps/TopicsIndexV2/components/TopicItem';
import { useTopicActionButton } from 'hb-react/shared/apps/TopicsIndexV2/hooks/useTopicActionButton';
import type { ITopic } from 'hb-react/shared/apps/TopicsIndexV2/types';

import RequestStatus from './RequestStatus';

export type TopicsWrapperProps = {
  data: ITopic;
  isBackOffice: boolean;
  isLoading: boolean;
} & Pick<
  PublicationLayoutsProps,
  'dynamicAttributes' | 'networkRootUrl' | 'whiteLabelUrl'
>;

const TopicsWrapper = ({
  data: fetchedTopic,
  dynamicAttributes,
  isBackOffice,
  isLoading,
  networkRootUrl,
  whiteLabelUrl,
}: TopicsWrapperProps) => {
  const breakpoint = useBreakpoint();
  const [topic, setTopic] = useState(fetchedTopic);
  const [displayDetailsModal, setDisplayDetailsModal] = useState(false);
  const [displayRequestModal, setDisplayRequestModal] = useState(false);
  const [requestApproval, setRequestApproval] = useState({
    id: topic?.approval_id,
  });

  const { currentUser } = useDefaultAppContexts();
  const isConnected = !!currentUser || isBackOffice;
  const requestIsPending = !!topic?.approval_id;
  const { buttonLabel, buttonA11yLabel } = useTopicActionButton(topic ?? {});

  useEffect(() => {
    setTopic(fetchedTopic);
  }, [fetchedTopic]);

  const href = useMemo(() => {
    if (isBackOffice) {
      return '#';
    }
    if (whiteLabelUrl) {
      return getTopicImmersiveMenuHref(
        whiteLabelUrl,
        topic?.landing_page,
        topic?.id,
      );
    }

    return `${networkRootUrl}${topic?.landing_page}`;
  }, [
    topic?.id,
    isBackOffice,
    networkRootUrl,
    topic?.landing_page,
    whiteLabelUrl,
  ]);

  if (isLoading || !topic) {
    return <PublicationCommonLoader />;
  }

  const handleOpenModal = () => {
    !isBackOffice && setDisplayDetailsModal(true);
  };

  const {
    categories,
    cover_picture_small_url: coverPicture,
    description,
    description_visible_in_frontoffice: descriptionVisibleInFrontOffice,
    location,
    name,
    restricted_access: restrictedAccess,
    total_followings: totalFollowings,
  } = topic;

  const target = whiteLabelUrl ? '_blank' : undefined;

  const {
    displayButtons,
    displayCategories,
    displayDescription,
    displayImage,
    displayLocation,
    displayNumberOfMembers,
    displayTitle,
  } = dynamicAttributes?.fields || {};

  const handleDisplayLocation = (location: {
    city?: string;
    country?: string;
  }): string | null => {
    if (!displayLocation) return null;
    if (!location?.city && !location?.country) return '';
    if (location?.city && !location?.country) return `${location.city}.`;
    if (!location?.city && location?.country) return `${location.country}.`;

    return `${location.city}, ${location.country}.`;
  };

  const noCategories = !displayCategories || categories.length === 0;
  const biggerScreen = breakpoint === 'l' || breakpoint === 'xl';
  // get mobile margin to display img in all the width.
  const MOBILE_CONTENT_MARGIN = 27;

  return (
    <Wrapper>
      <Header>
        <PictureWrapper
          $displayImage={!!displayImage}
          $mobileContentMargin={MOBILE_CONTENT_MARGIN}
          $noCategories={noCategories}
          onClick={handleOpenModal}
        >
          {displayImage && (
            <Picture
              data-testid="builder-cover-wrapper"
              $breakpoint={breakpoint}
              $mobileContentMargin={MOBILE_CONTENT_MARGIN}
              mb={!noCategories && biggerScreen ? 3 : 0}
            >
              <>
                <ImgWrapper>
                  <StyledImg
                    as="img"
                    src={coverPicture}
                    alt={__('cover picture')}
                  />
                </ImgWrapper>
                {restrictedAccess && (
                  <RequestStatus requestIsPending={requestIsPending} />
                )}
              </>
            </Picture>
          )}

          {!noCategories && biggerScreen && (
            <PillsList
              pills={categories}
              seeLessLabel={s__('Tags|See less')}
              seeMoreLabel={s__('Tags|See more')}
            />
          )}
        </PictureWrapper>
        <TitleWrapper>
          {displayTitle && (
            <Dotdotdot clamp={2} useNativeClamp>
              <TitleStyled onClick={handleOpenModal}>{name}</TitleStyled>
            </Dotdotdot>
          )}
          <Box mt={2}>
            {displayNumberOfMembers &&
              totalFollowings !== null &&
              n__(
                '%{totalFollowings} member',
                '%{totalFollowings} members',
                totalFollowings,
              ).i__({
                // @ts-expect-error totalFollowings is a number
                totalFollowings,
              })}
            {displayNumberOfMembers && handleDisplayLocation(location) && (
              <Box as="span" mx={1}>
                ·
              </Box>
            )}

            {handleDisplayLocation(location)}
          </Box>
          {displayButtons && (
            <Buttons $noCategories={noCategories}>
              {!topic.closed && (
                <Box width="100%" mr={3}>
                  <JoinGroupButton
                    isBackOffice={isBackOffice}
                    isConnected={isConnected}
                    labelButton={buttonLabel}
                    labelA11yButton={buttonA11yLabel}
                    setDisplayRequestModal={setDisplayRequestModal}
                    setTopics={(topics) => setTopic(topics[0])}
                    topic={topic}
                    topics={[topic]}
                  />
                </Box>
              )}
              <Box width="100%">
                <GoToGroupButtonWithoutContext
                  href={href}
                  target={target}
                  topicName={topic?.name}
                />
              </Box>
            </Buttons>
          )}
          {!noCategories && !biggerScreen && (
            <PillsList
              pills={categories}
              seeLessLabel={s__('Tags|See less')}
              seeMoreLabel={s__('Tags|See more')}
            />
          )}
        </TitleWrapper>
      </Header>
      {displayDescription &&
        description &&
        (isConnected || !descriptionVisibleInFrontOffice) && (
          <>
            <Divider mb={{ _: '14px', m: 3, l: 4 }} mt={4} />
            <Box width="100%" display="block">
              {description}
            </Box>
          </>
        )}

      {displayDetailsModal && (
        <TopicDetailsModalWithoutContext
          isConnectedUser={isConnected}
          setDisplayDetailsModal={setDisplayDetailsModal}
          topic={topic}
        >
          <Box ml={4} flexDirection="column" width={{ _: '283px', m: '209px' }}>
            <JoinGroupButton
              isBackOffice={isBackOffice}
              isConnected={isConnected}
              labelButton={buttonLabel}
              labelA11yButton={buttonA11yLabel}
              setDisplayRequestModal={setDisplayRequestModal}
              setTopics={(topics) => setTopic(topics[0])}
              topic={topic}
              topics={[topic]}
            />
            <Box mt={4}>
              <GoToGroupButtonWithoutContext
                href={href}
                target={target}
                topicName={topic?.name}
              />
            </Box>
          </Box>
        </TopicDetailsModalWithoutContext>
      )}

      {displayRequestModal && (
        <RequestGroupModal
          href={href}
          requestApproval={requestApproval}
          setDisplayRequestModal={setDisplayRequestModal}
          setRequestApproval={setRequestApproval}
          setTopics={(topics) => setTopic(topics[0])}
          target={target}
          topic={topic}
          topics={[topic]}
        />
      )}
    </Wrapper>
  );
};

export default TopicsWrapper;

const Buttons = styled(Box).attrs(
  ({ $noCategories }: { $noCategories: boolean }) => ({
    mt: { _: 4, l: 3 },
    maxWidth: '206px',
    mb: { _: !$noCategories ? 4 : 0, l: 0 },
  }),
)<{
  $noCategories: boolean;
}>``;

const Picture = styled(Box).attrs(
  ({ $mobileContentMargin }: { $mobileContentMargin: number }) => ({
    overflow: 'hidden',
    height: '100%',
    mt: {
      _: '-24px',
      m: 'unset',
    },
    width: {
      _: `calc(100% + ${$mobileContentMargin * 2}px)`,
      m: '100%',
      l: '468px',
      xl: '443px',
    },
  }),
)<{
  $mobileContentMargin: number;
  $breakpoint: string;
}>`
  border-radius: ${({
    theme,
    $breakpoint,
  }: {
    $breakpoint: string;
    theme: Theme;
  }) =>
    $breakpoint === 's'
      ? '0px'
      : `${theme.themeVariables.globalBorderRadius}px`};
`;

const PictureWrapper = styled(Box).attrs(
  ({
    $noCategories,
    $displayImage,
    $mobileContentMargin,
  }: {
    $displayImage: boolean;
    $mobileContentMargin: number;
    $noCategories: boolean;
  }) => ({
    flexDirection: 'column',
    width: !$noCategories || $displayImage ? '100%' : 'auto',
    mx: {
      _: `-${$mobileContentMargin}px`,
      m: 'unset',
    },
  }),
)<{
  $displayImage: boolean;
  $mobileContentMargin: number;
  $noCategories: boolean;
}>``;

const ImgWrapper = styled(Box).attrs({
  height: '100%',
  position: 'relative',
})`
  background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6));
  transition: 0.5s ease transform;
`;

const Header = styled(Box).attrs(() => ({
  justifyContent: 'space-between',
  alignItems: 'start',
  width: '100%',
  flexDirection: { _: 'column', l: 'row' },
}))`
  &:hover ${ImgWrapper} {
    transform: scale(1.2);
  }
`;

const StyledImg = styled(Box)`
  cursor: pointer;
  height: 100%;
  width: 100%;
`;

const TitleStyled = styled(Box).attrs({
  font: 'h1-semibold',
  display: 'block',
})`
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.link} !important;
  }
`;

const TitleWrapper = styled(Box).attrs({
  width: '100%',
  flexDirection: 'column',
  ml: { _: 0, l: 6 },
  mt: { _: 1, l: 6 },
})``;

const Wrapper = styled(Box).attrs(() => ({
  my: {
    s: 4,
    l: 6,
  },
  width: '100%',
  flexDirection: 'column',
}))``;
